import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import AddIcon from '@mui/icons-material/AddRounded'

import { AprysePDF } from '@libs/apryse/components/AprysePDF/AprysePDF.component'
import { MarkerPortal } from '@libs/apryse/components/Marker/MarkerPortal.component'
import { useMarker } from '@libs/apryse/hooks/useMarker.hook'
import { usePdfViewer } from '@libs/apryse/hooks/usePdfViewer.hook'
import { Coordinates, FitMode, Marker, MarkerType } from '@libs/apryse/type'

import { FormStatus, ObservationStatus, TaskStatus } from '@types'

import { AddMarkerButton, Container, FloatingMenuContainer } from './PdfMarkerEditor.styled'

type Config = {
  fitMode?: FitMode
  isReadOnly?: boolean
  disabledElements?: string[]
  canEdit?: boolean
  idNumber?: number
  markerColor?: string
  status?: ObservationStatus | FormStatus | TaskStatus
  markerType: MarkerType
}

interface PdfMarkerEditorProps {
  pdfUrl: string
  config: Config
  initialMarker?: Marker
  onAdd: (coordinates: Coordinates) => void
  onMarkerMoveEnd?: (coordinates: Coordinates) => void
  onDelete: () => void
}

export const PdfMarkerEditor: React.FC<PdfMarkerEditorProps> = ({
  pdfUrl,
  config,
  initialMarker,
  onMarkerMoveEnd,
  onDelete,
  onAdd,
}: PdfMarkerEditorProps) => {
  const { fitMode, isReadOnly, idNumber, disabledElements, markerColor, markerType, status, canEdit = true } = config

  const viewerRef = useRef<HTMLDivElement>(null)
  const previousPdfUrlRef = useRef<string>(pdfUrl)

  const { t } = useTranslation()

  const { markerContainer, instanceRef, pageNumber, isViewerInitialized, reloadDocument } = usePdfViewer({
    pdfUrl,
    viewerRef,
    config: {
      fitMode,
      isReadOnly,
      disabledElements,
      UIMode: 'simple',
    },
  })

  const { marker, markerStatus, dragState, eventHandlers, setMarkerStatus } = useMarker({
    instanceRef,
    pageNumber,
    initialMarker,
    idNumber,
    markerColor,
    status,
    markerType,
    onMarkerMoveEnd,
    onDelete,
    onAdd,
  })

  useEffect(() => {
    if (!isViewerInitialized || previousPdfUrlRef.current === pdfUrl) return

    reloadDocument(pdfUrl)

    previousPdfUrlRef.current = pdfUrl

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfUrl])

  const handleAddMarker = () => {
    setMarkerStatus('add_selecting_position')
  }

  const showAddMarkerButton = !marker && markerStatus === 'visible' && canEdit

  return (
    <Container>
      <AprysePDF viewerRef={viewerRef} />

      <MarkerPortal
        marker={marker}
        markerStatus={markerStatus}
        dragState={dragState}
        eventHandlers={eventHandlers}
        markerContainer={markerContainer}
        instanceRef={instanceRef}
        pageNumber={pageNumber}
        canEdit={canEdit}
      />

      {showAddMarkerButton && (
        <FloatingMenuContainer>
          <AddMarkerButton type="button" onClick={handleAddMarker}>
            <AddIcon />
            {t('modules_shared_components_PdfEditor_components_PdfMarkersEditor_add')}
          </AddMarkerButton>
        </FloatingMenuContainer>
      )}
    </Container>
  )
}
